/**
 * Sidebar · RAIOS Design System v13 (sessao 0384 aprovada · sessao 0415 implementada)
 *
 * Implementa as 7 decisoes de layout do v13 dashboard:
 *   #6  Sem topbar · raio amber como brand
 *   #7  Lua (toggle tema) no rodape da sidebar
 *   #8  200px expandida / 64px colapsada (frame 44x44)
 *   #9  Hamburger dinamico: rodape quando aberto (←) · topo quando colapsado (→)
 *   #10 Items aberto: icone 16px + texto 12.5px · padding 6×10 · gap 9 · min-height 28
 *   #11 Items colapsado: frame 44×44 · icone 20px centralizado
 *   #12 Groups: labels uppercase aberto · display:none colapsado + border-top separador
 *
 * Tokens consumidos: sidebar.* (design-tokens.ts)
 * Ref decisoes: raios/docs/design/DESIGN-SYSTEM.md § 19.2
 * Ref status: raios/docs/design/IMPLEMENTATION-MAP.md (Sidebar · decisoes 6-12)
 */

import { NavLink } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  MessageSquare,
  Briefcase,
  Blocks,
  Bot,
  BotMessageSquare,
  Box,
  ClipboardList,
  GitBranch,
  Key,
  Layers,
  Plug,
  Bell,
  Repeat,
  Server,
  Sparkles,
  Users,
  Activity,
  HeartPulse,
  Moon,
  ChevronLeft,
  ChevronRight,
  Wrench,
  type LucideIcon,
} from 'lucide-react';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  /** Indicador de estado visual opcional (ex: 'novo', 'legado', 'em breve'). */
  badge?: 'new' | 'legacy' | 'soon';
  /** Item visível mas não navegável (ex: Runtimes "em breve"). */
  disabled?: boolean;
}

export interface NavSection {
  title: string;
  items: NavItem[];
  /** Bloco renderizado apenas quando o tenant atual é admin. */
  adminOnly?: boolean;
}

/**
 * navSections — Reforma de UX 2026-06-10 (RAIOS-UX-SIDEBAR-SPEC.html v18 +
 * docs/RAIOS-UX-SIDEBAR-IMPLEMENTATION.md §3). 5 seções:
 *
 *   • Principal       = entrada do dia (Dashboard + Sessões)
 *   • Mission Control = operação cotidiana (Negócios, Demandas, Atividade)
 *   • Ativos          = recursos modelados/criáveis pelo tenant
 *   • Configurações   = setup do tenant (Contas LLM, Companions, MCP)
 *   • Admin           = ações sensíveis, gated por `tenant?.is_admin`
 *
 * Kanban, Pull Requests e Companion saíram do menu mas as rotas
 * continuam vivas por URL direta (decisão Breno #1/#2 — não deletar rota viva).
 */
// eslint-disable-next-line react-refresh/only-export-components
export const navSections: NavSection[] = [
  {
    title: 'Principal',
    items: [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
      { to: '/sessions', label: 'Sessões', icon: MessageSquare },
    ],
  },
  {
    title: 'Mission Control',
    items: [
      { to: '/businesses', label: 'Negócios', icon: Briefcase },
      { to: '/mc/demands', label: 'Demandas', icon: ClipboardList },
      { to: '/loops', label: 'Loops', icon: Repeat },
      { to: '/mc/activity', label: 'Atividade', icon: Activity },
    ],
  },
  {
    title: 'Ativos',
    items: [
      { to: '/agents', label: 'Agentes', icon: Bot },
      { to: '/skills', label: 'Skills', icon: Sparkles },
      { to: '/squads', label: 'Squads', icon: Users },
      { to: '/pipelines', label: 'Pipelines', icon: GitBranch },
    ],
  },
  {
    // Explosão da SettingsPage em 3 telas dedicadas (decisão §3.2 — rotas dedicadas).
    title: 'Configurações',
    items: [
      { to: '/settings/llm', label: 'Contas LLM', icon: Key },
      { to: '/settings/companions', label: 'Companions', icon: BotMessageSquare },
      { to: '/settings/mcp', label: 'MCP', icon: Plug },
      { to: '/settings/connectors', label: 'Conectores', icon: Blocks },
      { to: '/settings/notifications', label: 'Notificações', icon: Bell },
    ],
  },
  {
    // Bloco gated por tenant?.is_admin (via useAuth no componente).
    // Runtimes: visível mas desabilitado ("em breve") — decisão Breno #3;
    // a RuntimeNodesPage (/runtime/nodes) permanece viva fora do menu.
    title: 'Admin',
    adminOnly: true,
    items: [
      { to: '/admin/tools', label: 'Tools', icon: Wrench },
      { to: '/health', label: 'Health do Harness', icon: HeartPulse },
      { to: '/models', label: 'Modelos LLM', icon: Box },
      // Aponta para o modo TENANT (2026-07-27). A rota /admin/llm-resilience
      // exige um tenant_id UUID digitado à mão (`canLoad = isAdmin &&
      // UUID_RE.test(tenantId)`) e, sem ele, a página nem chama o backend — o
      // dono via a tela permanentemente em branco pelo menu. `/llm-resilience`
      // lista as tentativas do tenant autenticado direto, sem pedir nada.
      // O modo admin continua acessível por /admin/llm-resilience para
      // inspecionar OUTRO tenant, que é o caso de uso dele.
      { to: '/llm-resilience', label: 'Resiliência LLM', icon: Activity },
      { to: '/runtime/nodes', label: 'Runtimes', icon: Server, disabled: true, badge: 'soon' },
      { to: '/design-system', label: 'Design System', icon: Layers },
    ],
  },
];

/**
 * Fonte única do gate de navegação. Desktop e mobile só adaptam o layout;
 * visibilidade e semântica dos itens vêm sempre do mesmo catálogo.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function getVisibleNavSections(isAdmin: boolean): NavSection[] {
  return navSections.filter((section) => !section.adminOnly || isAdmin);
}

interface SidebarProps {
  collapsed?: boolean;
  /** Callback quando usuario clica no hamburger · permite pai controlar estado. */
  onToggleCollapsed?: () => void;
  /** Callback quando usuario clica na lua · toggle de tema. */
  onToggleTheme?: () => void;
  className?: string;
}

export function Sidebar({
  collapsed = false,
  onToggleCollapsed,
  onToggleTheme,
  className,
}: SidebarProps) {
  // Gate Admin (DEM-1870): reaproveita padrão de DashboardPage e
  // AdminPromotionsPage — tenant?.is_admin via useAuth(). Bloco com
  // `adminOnly: true` é filtrado quando o tenant não é admin.
  const { tenant } = useAuth();
  const isAdmin = tenant?.is_admin ?? false;
  const visibleSections = getVisibleNavSections(isAdmin);

  return (
    <aside
      className={cn(
        // Background gradient sutil · ghost border direita · full height
        'flex flex-col shrink-0 h-full overflow-hidden transition-[width] duration-200 ease-out',
        'border-r border-raios-border',
        'bg-gradient-to-b from-[#0D0E12] to-[#0A0B0F]',
        className,
      )}
      style={{
        width: collapsed ? 64 : 200,
        willChange: 'width',
      }}
      aria-label="Navegação principal"
    >
      {/* ═══════════════════════════════════════════════════════════════
          TOP · Brand (raio ⚡ amber · Outfit 14px 700)
          Decisao v13 #6: sem topbar · raio amber como brand na sidebar
          ═══════════════════════════════════════════════════════════════ */}
      <div
        className={cn(
          'flex items-center border-b border-raios-border transition-[padding] duration-150',
          collapsed
            ? 'flex-col gap-1 justify-center py-2 min-h-[auto]'
            : 'gap-2 px-3.5 pt-3.5 pb-3 min-h-[52px]',
        )}
      >
        {/* Brand expandido: raio + texto "RAIOS" */}
        {!collapsed && (
          <span
            className="flex items-center gap-2 font-display text-[14px] font-bold tracking-[-0.02em] text-raios-text whitespace-nowrap"
          >
            <img
              src="/raios-bolt.png"
              alt=""
              className="h-4 w-4"
              style={{ filter: 'drop-shadow(0 0 8px rgba(251, 191, 36, 0.35))' }}
              aria-hidden="true"
            />
            RAIOS
          </span>
        )}

        {/* Brand colapsado: frame 44×44 com raio centralizado */}
        {collapsed && (
          <span
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px]"
            style={{
              background: 'rgba(251, 191, 36, 0.12)',
              border: '1px solid rgba(251, 191, 36, 0.3)',
            }}
            aria-label="RAIOS"
          >
            <img
              src="/raios-bolt.png"
              alt=""
              className="h-5 w-5"
              aria-hidden="true"
            />
          </span>
        )}

        {/* Hamburger no topo quando colapsado — decisao v13 #9 */}
        {collapsed && onToggleCollapsed && (
          <button
            type="button"
            onClick={onToggleCollapsed}
            className="flex h-7 w-11 shrink-0 items-center justify-center rounded-md p-0 text-raios-muted transition-colors hover:bg-raios-elevated hover:text-raios-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-raios-accent/40"
            aria-label="Expandir sidebar"
            title="Expandir"
          >
            <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
          </button>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          BODY · Grupos + items
          Decisoes v13 #10 (items aberto) + #11 (colapsado frame 44×44) + #12 (groups)
          ═══════════════════════════════════════════════════════════════ */}
      <nav
        className={cn(
          'flex-1 overflow-y-auto flex flex-col',
          collapsed ? 'px-1.5 py-2.5 items-center gap-0' : 'px-2 py-2.5 gap-[1px]',
        )}
      >
        {visibleSections.map((section, sectionIdx) => (
          <div
            key={section.title}
            className={cn(
              'w-full',
              collapsed && 'flex flex-col items-center',
              // v11: separador border-top entre grupos apenas quando colapsado (decisao v13 #12)
              collapsed && sectionIdx > 0 && 'mt-2 pt-2 border-t border-raios-border',
            )}
            style={{ marginBottom: 2 }}
          >
            {/* Group label (uppercase tracking amplo) — some no colapsado */}
            {!collapsed && (
              <div
                className="text-raios-muted font-semibold whitespace-nowrap overflow-hidden"
                style={{
                  fontSize: '9px',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  padding: '8px 8px 3px',
                }}
              >
                {section.title}
              </div>
            )}

            {/* Items */}
            {section.items.map((item) => item.disabled ? (
              // Item desabilitado (ex: Runtimes "em breve") — visível, não navegável.
              <div
                key={item.to}
                aria-disabled="true"
                title={collapsed ? `${item.label} (em breve)` : undefined}
                className={cn(
                  'relative w-full flex items-center select-none overflow-hidden whitespace-nowrap cursor-not-allowed opacity-50 text-raios-text-secondary',
                  collapsed ? 'justify-center rounded-[10px]' : 'rounded-[5px]',
                )}
                style={
                  collapsed
                    ? { width: 44, height: 44, minHeight: 44, padding: 0, gap: 0 }
                    : { padding: '6px 10px', fontSize: '12.5px', minHeight: 28, gap: 9 }
                }
              >
                <span
                  className="flex shrink-0 items-center justify-center text-raios-muted"
                  style={{ width: collapsed ? 20 : 18 }}
                >
                  <item.icon
                    style={{ width: collapsed ? 20 : 16, height: collapsed ? 20 : 16 }}
                    strokeWidth={1.8}
                    aria-hidden="true"
                  />
                </span>
                {!collapsed && (
                  <span className="flex-1 overflow-hidden whitespace-nowrap">{item.label}</span>
                )}
                {!collapsed && item.badge === 'soon' && (
                  <span
                    className="text-[8.5px] font-medium uppercase tracking-wider text-raios-muted px-1 py-px rounded-sm bg-raios-elevated/50"
                    style={{ letterSpacing: '0.06em' }}
                  >
                    em breve
                  </span>
                )}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                title={collapsed ? item.label : undefined}
                className={({ isActive }) =>
                  cn(
                    'relative w-full flex items-center select-none transition-colors duration-150 cursor-pointer overflow-hidden whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-raios-accent/40',
                    collapsed
                      ? 'justify-center rounded-[10px]'
                      : 'rounded-[5px]',
                    isActive
                      ? collapsed
                        ? 'bg-raios-accent/15 text-raios-accent-text'
                        : 'bg-raios-accent/10 text-raios-accent-text font-medium'
                      : 'text-raios-text-secondary hover:bg-raios-elevated hover:text-raios-text',
                  )
                }
                style={
                  collapsed
                    ? { width: 44, height: 44, minHeight: 44, padding: 0, gap: 0 }
                    : { padding: '6px 10px', fontSize: '12.5px', minHeight: 28, gap: 9 }
                }
              >
                {({ isActive }) => (
                  <>
                    {/* Barra lateral accent (2px) quando ativo · só no aberto (v13) */}
                    {isActive && !collapsed && (
                      <span
                        aria-hidden="true"
                        className="absolute left-0 rounded-r-[2px] bg-raios-accent"
                        style={{ top: 5, bottom: 5, width: 2 }}
                      />
                    )}

                    {/* Icone · 16px aberto · 20px colapsado (v13 #10, #11) */}
                    <span
                      className={cn(
                        'flex shrink-0 items-center justify-center transition-colors',
                        isActive
                          ? 'text-raios-accent-text'
                          : 'text-raios-muted',
                      )}
                      style={{ width: collapsed ? 20 : 18 }}
                    >
                      <item.icon
                        style={{
                          width: collapsed ? 20 : 16,
                          height: collapsed ? 20 : 16,
                        }}
                        strokeWidth={1.8}
                        aria-hidden="true"
                      />
                    </span>

                    {/* Label · display:none colapsado (v13 #10 · fix v13 FIX) */}
                    {!collapsed && (
                      <span className="flex-1 overflow-hidden whitespace-nowrap">
                        {item.label}
                      </span>
                    )}

                    {/* Badge discreto (new/legacy) — só no aberto */}
                    {!collapsed && item.badge === 'legacy' && (
                      <span
                        className="text-[8.5px] font-medium uppercase tracking-wider text-raios-muted px-1 py-px rounded-sm bg-raios-elevated/50"
                        style={{ letterSpacing: '0.06em' }}
                      >
                        legado
                      </span>
                    )}

                    {isActive && <span className="sr-only">(página atual)</span>}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      {/* ═══════════════════════════════════════════════════════════════
          FOOTER · Hamburger (expandido) + Lua (toggle tema)
          Decisoes v13 #7 (lua rodape) + #9 (hamburger rodape aberto)
          ═══════════════════════════════════════════════════════════════ */}
      <div
        className={cn(
          'border-t border-raios-border flex items-center transition-[padding] duration-150',
          collapsed ? 'flex-col gap-2 py-2 justify-center' : 'justify-between px-2.5 py-2.5',
        )}
      >
        {/* Hamburger no rodape quando expandido (decisao v13 #9) · some no colapsado */}
        {!collapsed && onToggleCollapsed && (
          <button
            type="button"
            onClick={onToggleCollapsed}
            className="flex h-7 w-7 items-center justify-center rounded-md p-0 text-raios-muted transition-colors hover:bg-raios-elevated hover:text-raios-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-raios-accent/40"
            aria-label="Colapsar sidebar"
            title="Colapsar"
          >
            <ChevronLeft className="h-[15px] w-[15px]" strokeWidth={2} aria-hidden="true" />
          </button>
        )}

        {/* Lua · toggle tema (sempre presente · decisao v13 #7) */}
        <button
          type="button"
          onClick={onToggleTheme}
          className={cn(
            'flex items-center justify-center rounded-md p-0 text-raios-muted transition-colors hover:bg-raios-elevated hover:text-raios-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-raios-accent/40',
            collapsed ? 'h-7 w-7' : 'h-7 w-7',
          )}
          aria-label="Alternar tema"
          title="Alternar tema"
        >
          <Moon className="h-[15px] w-[15px]" strokeWidth={2} aria-hidden="true" />
        </button>
      </div>
    </aside>
  );
}
